import type { NoteEntity } from '@/entities/Notes';

import type { PdfDocumentNote, PdfNoteAnchorType, PdfNoteHighlightColor, PdfNotePoint } from './types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toNumberOrNull = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const parsePoint = (value: unknown): PdfNotePoint | null => {
  if (!isRecord(value)) {
    return null;
  }

  const x = toNumberOrNull(value.x);
  const y = toNumberOrNull(value.y);

  if (x === null || y === null) {
    return null;
  }

  return { x, y };
};

const parsePathFromString = (rawPath: string | null): PdfNotePoint[] | undefined => {
  if (!rawPath) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(rawPath);

    if (!Array.isArray(parsed)) {
      return undefined;
    }

    const normalized = parsed.map((item) => parsePoint(item)).filter((item): item is PdfNotePoint => item !== null);

    return normalized.length > 1 ? normalized : undefined;
  } catch {
    return undefined;
  }
};

const getDocumentId = (note: NoteEntity): string | null => {
  if (note.artifactType === 'document' && note.artifactId) {
    return note.artifactId;
  }

  return null;
};

const getPageNumber = (note: NoteEntity): number => {
  if (typeof note.anchorPageNumber === 'number' && Number.isFinite(note.anchorPageNumber)) {
    return Math.max(1, Math.floor(note.anchorPageNumber));
  }

  return 1;
};

const resolveAnchorType = ({
  anchorType,
  path,
  point,
  rectsCount,
}: {
  anchorType: NoteEntity['anchorType'];
  path?: PdfNotePoint[];
  point?: PdfNotePoint;
  rectsCount: number;
}): PdfNoteAnchorType | undefined => {
  if (anchorType === 'text' || anchorType === 'point' || anchorType === 'area') {
    return anchorType;
  }

  if (path && path.length > 1) {
    return 'area';
  }

  if (point) {
    return 'point';
  }

  if (rectsCount > 0) {
    return 'text';
  }

  return undefined;
};

export const toPdfDocumentNote = (note: NoteEntity): PdfDocumentNote | null => {
  const documentId = getDocumentId(note);

  if (!documentId) {
    return null;
  }

  const point = note.anchorPoint ?? undefined;
  const path = parsePathFromString(note.anchorPath);
  const rects = note.anchorRects;
  const color =
    note.anchorColor &&
    (note.anchorColor === 'blue' ||
      note.anchorColor === 'yellow' ||
      note.anchorColor === 'green' ||
      note.anchorColor === 'red' ||
      note.anchorColor === 'orange')
      ? (note.anchorColor as PdfNoteHighlightColor)
      : undefined;

  return {
    id: String(note.id),
    organizationId: '',
    documentId,
    caption: note.caption,
    text: note.text,
    pageNumber: getPageNumber(note),
    quote: note.anchorQuote,
    rects,
    anchorType: resolveAnchorType({
      anchorType: note.anchorType,
      path,
      point,
      rectsCount: rects.length,
    }),
    point,
    path,
    color,
    createdAt: note.createdAt,
    updatedAt: note.updatedAt,
  };
};

export const toPdfDocumentNotes = (notes: NoteEntity[], organizationId: string): PdfDocumentNote[] =>
  notes
    .map((note) => {
      const mapped = toPdfDocumentNote(note);

      if (!mapped) {
        return null;
      }

      return {
        ...mapped,
        organizationId,
      };
    })
    .filter((item): item is PdfDocumentNote => item !== null);

export const toBackendAnchorPath = (path?: PdfNotePoint[]): string | null => {
  if (!path || path.length < 2) {
    return null;
  }

  return JSON.stringify(path);
};

export const resolveCreateAnchorType = (payload: {
  anchorType?: PdfNoteAnchorType;
  point?: PdfNotePoint;
  path?: PdfNotePoint[];
  rects: PdfDocumentNote['rects'];
}): PdfNoteAnchorType | null => {
  if (payload.path && payload.path.length > 1) {
    return 'area';
  }

  if (payload.point) {
    return 'point';
  }

  if (payload.anchorType === 'text' || payload.rects.length > 0) {
    return 'text';
  }

  return payload.anchorType ?? null;
};
